import { Injectable, OnDestroy, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import type { AppUserRole } from '@aveespa/common/models/auth/app-roles.interface';
import type { UserClaims } from '@aveespa/common/models/tenant/user-claims.interface';
import {
    Observable,
    Subject,
    distinctUntilChanged,
    map,
    shareReplay,
} from 'rxjs';

/**
 * Single source of truth for the current user's tenant identity.
 *
 * Claims are read from the Firebase ID token, which is the **server-authoritative**
 * source for tenantId and role — never from Firestore or component state.
 *
 * The observable `claims$` is built on `onIdTokenChanged`, so it automatically
 * re-emits whenever:
 *   - The user signs in or out.
 *   - The token auto-refreshes (every ~1 h).
 *   - `forceClaimsRefresh()` is called explicitly (e.g. after `assignUserToTenant`).
 *
 * Usage in a data service:
 *   constructor(private _tenantCtx: TenantContextService) {}
 *
 *   getData(): Observable<Item[]> {
 *     return this._tenantCtx.tenantId$.pipe(
 *       filter(Boolean),
 *       switchMap(tenantId => this._readFromFirestore(tenantId)),
 *     );
 *   }
 */
@Injectable({ providedIn: 'root' })
export class TenantContextService implements OnDestroy {
    private readonly _auth = inject(Auth);
    private readonly _destroy$ = new Subject<void>();

    /**
     * Synchronous snapshot of the last decoded claims.
     * Populated the moment `onIdTokenChanged` resolves, which happens before
     * TenantGuard resolves and before any protected component is rendered.
     * Safe to call synchronously from service constructors on protected routes.
     */
    private _snapshot: UserClaims | null = null;

    /**
     * Emits the decoded custom claims on every token change.
     * Emits `null` when the user is signed out or when claims are absent
     * (e.g. user not yet assigned to a tenant).
     */
    readonly claims$: Observable<UserClaims | null> = new Observable<UserClaims | null>(
        (subscriber) => {
            // onIdTokenChanged fires on sign-in, sign-out, auto-refresh, and
            // any forced refresh via getIdToken(true).
            const unsubscribe = this._auth.onIdTokenChanged(async (firebaseUser) => {
                if (!firebaseUser) {
                    this._snapshot = null;
                    subscriber.next(null);
                    return;
                }
                try {
                    // forceRefresh=false — token was already refreshed by the event;
                    // just decode what is cached locally.
                    const result = await firebaseUser.getIdTokenResult();
                    const { tenantId, role } = result.claims as Partial<UserClaims>;

                    if (tenantId && role) {
                        const claims = { tenantId, role } as UserClaims;
                        this._snapshot = claims;
                        subscriber.next(claims);
                    } else {
                        // Authenticated but not yet assigned to a tenant.
                        this._snapshot = null;
                        subscriber.next(null);
                    }
                } catch {
                    this._snapshot = null;
                    subscriber.next(null);
                }
            });

            // Return the Firebase unsubscribe function as the teardown logic.
            return unsubscribe;
        },
    ).pipe(
        // Keep the last emitted value for late subscribers (e.g. guards resolving
        // after the token has already been refreshed at login time).
        shareReplay(1),
    );

    /**
     * Synchronous accessor for the current tenantId.
     *
     * Returns the tenantId from the last decoded token, or `null` if the user
     * is not signed in or has no tenant assigned yet.
     *
     * Safe to call in service constructors on routes protected by TenantGuard,
     * because the guard ensures the token has been decoded before any child
     * component or service is instantiated.
     */
    get currentTenantId(): string | null {
        return this._snapshot?.tenantId ?? null;
    }

    /**
     * Synchronous accessor for the current role.
     *
     * Returns the role from the last decoded token, or `null` if the user
     * is not signed in or has no claims assigned yet.
     *
     * Safe to call in service constructors on routes protected by TenantGuard.
     */
    get currentRole(): AppUserRole | null {
        return this._snapshot?.role ?? null;
    }

    /** Emits the current tenantId, or `null` when not available. */
    readonly tenantId$: Observable<string | null> = this.claims$.pipe(
        map((claims) => claims?.tenantId ?? null),
        distinctUntilChanged(),
    );

    /** Emits the current role, or `null` when not available. */
    readonly role$: Observable<AppUserRole | null> = this.claims$.pipe(
        map((claims) => claims?.role ?? null),
        distinctUntilChanged(),
    );

    /**
     * Forces a token refresh and waits for the new claims to be emitted.
     *
     * Call this immediately after `assignUserToTenant` resolves on the admin side,
     * so the current session picks up the updated tenantId/role without waiting
     * for the natural ~1 h auto-refresh.
     *
     * `claims$` will emit the new value automatically because `getIdToken(true)`
     * triggers `onIdTokenChanged`, which is what `claims$` listens to.
     */
    async forceClaimsRefresh(): Promise<void> {
        const firebaseUser = this._auth.currentUser;
        if (firebaseUser) {
            await firebaseUser.getIdToken(/* forceRefresh */ true);
        }
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }
}
